import axios from 'axios';
import { getBackendUrl } from '../config/apiConfig';
import { buildQueryParams } from '../Components/Utils/urlParams';

/**
 * Admin API Service
 * Centralized service for all admin-related API calls
 *
 * This service extracts API logic from adminState.js to:
 * 1. Improve testability (easier to mock)
 * 2. Reduce adminState.js complexity
 * 3. Enable reuse across different contexts
 * 4. Provide consistent error handling
 */

const backendUrl = getBackendUrl();

// ==================== Form Endpoints ====================

/**
 * Get all submitted forms (non-paginated)
 * @returns {Promise<Object>} Form data
 */
export const fetchAllForms = async () => {
  const response = await axios.get(`${backendUrl}/api/v1/admin/forms/getForms`);
  return response.data;
};

/**
 * Get submitted forms for a specific user (non-paginated)
 * @param {number} userId - Portal user ID
 * @returns {Promise<Object>} Form data for the user
 */
export const fetchFormsByUser = async (userId) => {
  if (!userId) {
    throw new Error('User ID is required');
  }
  const response = await axios.get(
    `${backendUrl}/api/v1/admin/forms/getFormsByPortalUser/${userId}`
  );
  return response.data;
};

/**
 * Get paginated forms with filters
 * @param {Object} params - Query parameters
 * @param {number} [params.page=0] - Page number (0-indexed)
 * @param {number} [params.size=20] - Page size
 * @param {string} [params.sortBy='id'] - Sort field
 * @param {string} [params.sortDirection='asc'] - Sort direction
 * @param {string} [params.formName='mileage'] - Form type to filter
 * @returns {Promise<Object>} Paginated form response
 */
export const fetchFormsPaginated = async (params = {}) => {
  const {
    page = 0,
    size = 20,
    sortBy = 'id',
    sortDirection = 'asc',
    formName = 'mileage',
  } = params;

  const queryString = buildQueryParams(
    { page, size, sortBy, sortDirection, formName },
    ['page', 'size', 'sortBy', 'sortDirection', 'formName']
  );

  const response = await axios.get(
    `${backendUrl}/api/v1/admin/forms/getFormsPaginated?${queryString}`
  );
  return response.data;
};

/**
 * Get paginated forms for a specific user
 * @param {number} portalUserId - Portal user ID
 * @param {Object} params - Query parameters (same as fetchFormsPaginated)
 * @returns {Promise<Object>} Paginated form response
 */
export const fetchFormsPaginatedByUser = async (portalUserId, params = {}) => {
  if (!portalUserId) {
    throw new Error('Portal user ID is required');
  }

  const {
    page = 0,
    size = 20,
    sortBy = 'id',
    sortDirection = 'asc',
    formName = 'mileage',
  } = params;

  const queryString = buildQueryParams(
    { page, size, sortBy, sortDirection, formName },
    ['page', 'size', 'sortBy', 'sortDirection', 'formName']
  );

  const response = await axios.get(
    `${backendUrl}/api/v1/admin/forms/getFormsPaginatedByPortalUser/${portalUserId}?${queryString}`
  );
  return response.data;
};

/**
 * Get recent forms across all form types
 * @param {number} [size=10] - Number of recent forms to fetch per type
 * @returns {Promise<Array>} Array of recent forms sorted by date
 */
export const fetchRecentForms = async (size = 10) => {
  const formNames = [
    'backpay',
    'bonus',
    'hotel',
    'mileage',
    'pto',
    'newhire',
    'perdiem',
    'termination',
    'targetorder',
    'uniform',
    'workticket',
    'nonmnmileage',
  ];

  const requests = formNames.map((fn) => {
    const query = new URLSearchParams({
      page: '0',
      size: size.toString(),
      sortBy: 'submittedDate',
      sortDirection: 'desc',
      formName: fn,
    });
    return axios
      .get(`${backendUrl}/api/v1/admin/forms/getFormsPaginated?${query}`)
      .then((res) => ({ fn, data: res.data }))
      .catch(() => ({ fn, data: null }));
  });

  const results = await Promise.all(requests);

  const flatForms = [];
  results.forEach(({ fn, data }) => {
    if (!data) return;
    const pageKey = Object.keys(data).find((k) => k.endsWith('Page'));
    const page = pageKey ? data[pageKey] : null;
    const content = page?.content || [];
    content.forEach((form) => flatForms.push({ ...form, formName: fn }));
  });

  flatForms.sort((a, b) => {
    const aDate = new Date(a.submittedDate || a.created || 0).getTime();
    const bDate = new Date(b.submittedDate || b.created || 0).getTime();
    return bDate - aDate;
  });

  return flatForms.slice(0, size);
};

/**
 * Get form count summary
 * @returns {Promise<Object>} Form count by type
 */
export const fetchFormCount = async () => {
  const response = await axios.get(`${backendUrl}/api/v1/admin/forms/count`);
  return response.data;
};

/**
 * Submit or update a form
 * @param {Object|FormData} body - Form data
 * @param {string} form - Form type (e.g., 'hotel', 'mileage')
 * @param {string} department - Department (e.g., 'admin', 'cbm')
 * @returns {Promise<Object>} Submission response
 */
export const submitForm = async (body, form, department) => {
  if (!body || !form || !department) {
    throw new Error(
      'Missing required parameters: body, form, and department are required'
    );
  }

  const config = {};
  // Only set Content-Type for newHire (multipart), axios interceptor handles Authorization
  if (form === 'newHire') {
    config.headers = { 'Content-Type': 'multipart/form-data' };
  }

  const response = await axios.post(
    `${backendUrl}/api/v1/${department}/${form}Submit`,
    body,
    config
  );
  return response;
};

// ==================== User Endpoints ====================

/**
 * Get all portal users
 * @returns {Promise<Array>} List of portal users
 */
export const fetchAllUsers = async () => {
  const response = await axios.get(`${backendUrl}/api/v1/admin/getPortalUsers`);
  return response.data;
};

/**
 * Get all active districts
 * @returns {Promise<Array>} List of district safe view objects
 */
export const fetchAllDistricts = async () => {
  const response = await axios.get(`${backendUrl}/api/v1/admin/districts`);
  return response.data;
};

/**
 * Disable a district
 * @param {number} districtId - District ID
 * @returns {Promise<void>}
 */
export const deleteDistrict = async (districtId) => {
  if (!districtId) {
    throw new Error('District ID is required');
  }
  const response = await axios.delete(
    `${backendUrl}/api/v1/admin/districts/${districtId}`
  );
  return response.data;
};

/**
 * Re-enable a district
 * @param {number} districtId - District ID
 * @returns {Promise<void>}
 */
export const enableDistrict = async (districtId) => {
  if (!districtId) {
    throw new Error('District ID is required');
  }
  const response = await axios.put(
    `${backendUrl}/api/v1/admin/districts/${districtId}/enable`
  );
  return response.data;
};

/**
 * Get all active sub-areas
 * @returns {Promise<Array>} List of active sub-areas
 */
export const fetchAllSubAreas = async () => {
  const response = await axios.get(`${backendUrl}/api/v1/admin/sub-areas`);
  return response.data;
};

/**
 * Get a sub-area by id
 * @param {number} subAreaId - Sub-area ID
 * @returns {Promise<Object>} Sub-area data
 */
export const fetchSubAreaById = async (subAreaId) => {
  if (!subAreaId) {
    throw new Error('Sub-area ID is required');
  }
  const response = await axios.get(
    `${backendUrl}/api/v1/admin/sub-areas/${subAreaId}`
  );
  return response.data;
};

/**
 * Get active sub-areas for a district
 * @param {number} districtId - District ID
 * @returns {Promise<Array>} List of sub-areas in the district
 */
export const fetchSubAreasByDistrict = async (districtId) => {
  if (!districtId) {
    throw new Error('District ID is required');
  }
  const response = await axios.get(
    `${backendUrl}/api/v1/admin/sub-areas/district/${districtId}`
  );
  return response.data;
};

/**
 * Create a sub-area
 * @param {Object} subArea - Sub-area payload
 * @returns {Promise<Object>} Created sub-area
 */
export const createSubArea = async (subArea) => {
  if (!subArea?.name || !subArea?.district?.id) {
    throw new Error('Sub-area name and district.id are required');
  }
  const response = await axios.post(
    `${backendUrl}/api/v1/admin/sub-areas`,
    subArea,
    { headers: { 'Content-Type': 'application/json' } }
  );
  return response.data;
};

/**
 * Update a sub-area
 * @param {number} subAreaId - Sub-area ID
 * @param {Object} subArea - Sub-area payload
 * @returns {Promise<Object>} Updated sub-area
 */
export const updateSubArea = async (subAreaId, subArea) => {
  if (!subAreaId) {
    throw new Error('Sub-area ID is required');
  }
  if (!subArea?.name || !subArea?.district?.id) {
    throw new Error('Sub-area name and district.id are required');
  }
  const response = await axios.put(
    `${backendUrl}/api/v1/admin/sub-areas/${subAreaId}`,
    subArea,
    { headers: { 'Content-Type': 'application/json' } }
  );
  return response.data;
};

/**
 * Disable a sub-area
 * @param {number} subAreaId - Sub-area ID
 * @returns {Promise<void>}
 */
export const deleteSubArea = async (subAreaId) => {
  if (!subAreaId) {
    throw new Error('Sub-area ID is required');
  }
  const response = await axios.delete(
    `${backendUrl}/api/v1/admin/sub-areas/${subAreaId}`
  );
  return response.data;
};

/**
 * Add a store to a sub-area
 * @param {number} subAreaId - Sub-area ID
 * @param {number} storeId - Store ID
 * @returns {Promise<Object>} Updated sub-area
 */
export const addStoreToSubArea = async (subAreaId, storeId) => {
  if (!subAreaId || !storeId) {
    throw new Error('Sub-area ID and store ID are required');
  }
  const response = await axios.post(
    `${backendUrl}/api/v1/admin/sub-areas/${subAreaId}/stores/${storeId}`
  );
  return response.data;
};

/**
 * Remove a store from a sub-area
 * @param {number} subAreaId - Sub-area ID
 * @param {number} storeId - Store ID
 * @returns {Promise<Object>} Updated sub-area
 */
export const removeStoreFromSubArea = async (subAreaId, storeId) => {
  if (!subAreaId || !storeId) {
    throw new Error('Sub-area ID and store ID are required');
  }
  const response = await axios.delete(
    `${backendUrl}/api/v1/admin/sub-areas/${subAreaId}/stores/${storeId}`
  );
  return response.data;
};

/**
 * Get a specific portal user by username
 * @param {string} userName - Username to look up
 * @returns {Promise<Object|null>} User data or null
 */
export const fetchUserByUsername = async (userName) => {
  if (!userName) {
    throw new Error('Username is required');
  }
  const response = await axios.get(
    `${backendUrl}/api/v1/admin/getPortalUser?userName=${encodeURIComponent(
      userName
    )}`
  );
  return response.data;
};

/**
 * Delete a portal user (soft delete)
 * @param {number} userId - User ID to delete
 * @returns {Promise<Object>} Deletion response
 */
export const deleteUser = async (userId) => {
  if (!userId) {
    throw new Error('User ID is required');
  }
  const response = await axios.delete(
    `${backendUrl}/api/v1/admin/deletePortalUser/${userId}`
  );
  return response.data;
};

/**
 * Assign additional authorities to a user
 * @param {number} userId - User ID
 * @param {string[]} authorities - Authorities to add
 * @returns {Promise<Object>} Updated user data
 */
export const assignUserAuthorities = async (userId, authorities) => {
  if (!userId) {
    throw new Error('User ID is required');
  }
  if (!authorities || authorities.length === 0) {
    throw new Error('At least one authority is required');
  }
  const response = await axios.post(
    `${backendUrl}/api/v1/admin/assignAuthorities/${userId}`,
    { authorities },
    { headers: { 'Content-Type': 'application/json' } }
  );
  return response.data;
};

/**
 * Replace all authorities for a user
 * @param {number} userId - User ID
 * @param {string[]} authorities - New authorities (replaces existing)
 * @returns {Promise<Object>} Updated user data
 */
export const replaceUserAuthorities = async (userId, authorities) => {
  if (!userId) {
    throw new Error('User ID is required');
  }
  if (!authorities || authorities.length === 0) {
    throw new Error('At least one authority is required');
  }
  const response = await axios.put(
    `${backendUrl}/api/v1/admin/replaceAuthorities/${userId}`,
    { authorities },
    { headers: { 'Content-Type': 'application/json' } }
  );
  return response.data;
};

/**
 * Assign sub-areas to a user
 * @param {number} userId - User ID
 * @param {number[]} subAreaIds - Sub-area IDs to add
 * @returns {Promise<Object>} Updated portal user
 */
export const assignSubAreasToUser = async (userId, subAreaIds = []) => {
  if (!userId) {
    throw new Error('User ID is required');
  }
  const response = await axios.post(
    `${backendUrl}/api/v1/admin/assignSubAreas/${userId}`,
    subAreaIds,
    { headers: { 'Content-Type': 'application/json' } }
  );
  return response.data;
};

/**
 * Replace a user's sub-area assignments
 * @param {number} userId - User ID
 * @param {number[]|null} subAreaIds - Sub-area IDs to set, or null to clear
 * @returns {Promise<Object>} Updated portal user
 */
export const replaceSubAreasForUser = async (userId, subAreaIds = []) => {
  if (!userId) {
    throw new Error('User ID is required');
  }
  const response = await axios.put(
    `${backendUrl}/api/v1/admin/replaceSubAreas/${userId}`,
    subAreaIds,
    { headers: { 'Content-Type': 'application/json' } }
  );
  return response.data;
};

// ==================== Store Endpoints ====================

/**
 * Get all stores
 * @returns {Promise<Array>} List of stores
 */
export const fetchAllStores = async () => {
  const response = await axios.get(`${backendUrl}/api/stores/v1/getStores`);
  return response.data;
};

/**
 * Delete a store
 * @param {number} storeId - Store ID to delete
 * @returns {Promise<Object>} Deletion response
 */
export const deleteStore = async (storeId) => {
  if (!storeId) {
    throw new Error('Store ID is required');
  }
  const response = await axios.delete(
    `${backendUrl}/api/stores/v1/deleteStore/${storeId}`
  );
  return response.data;
};

// ==================== Session Endpoints ====================

/**
 * Get all active sessions
 * @returns {Promise<Array>} List of active sessions
 */
export const fetchAllSessions = async () => {
  const response = await axios.get(`${backendUrl}/api/v1/sessions/all`);
  return response.data;
};

/**
 * Revoke a specific session
 * @param {string} sessionId - Session ID to revoke
 * @returns {Promise<Object>} Revocation response
 */
export const revokeSession = async (sessionId) => {
  if (!sessionId) {
    throw new Error('Session ID is required');
  }
  const response = await axios.delete(
    `${backendUrl}/api/v1/sessions/${sessionId}`
  );
  return response.data;
};

/**
 * Cleanup expired tokens
 * @returns {Promise<Object>} Cleanup response with deleted count
 */
export const cleanupExpiredTokens = async () => {
  const response = await axios.post(`${backendUrl}/api/v1/sessions/cleanup`);
  return response.data;
};

// ==================== Utility Functions ====================

/**
 * Calculate session statistics from session list
 * @param {Array} sessions - List of sessions
 * @returns {Object} Session statistics
 */
export const calculateSessionStats = (sessions) => {
  if (!sessions || !Array.isArray(sessions)) {
    return {
      totalActiveSessions: 0,
      uniqueUsers: 0,
      browserCounts: {},
      osCounts: {},
    };
  }

  const stats = {
    totalActiveSessions: sessions.length,
    uniqueUsers: new Set(sessions.map((s) => s.userName || s.username)).size,
    browserCounts: {},
    osCounts: {},
  };

  sessions.forEach((session) => {
    const browser = session.browser || 'Unknown';
    const os = session.operatingSystem || 'Unknown';
    stats.browserCounts[browser] = (stats.browserCounts[browser] || 0) + 1;
    stats.osCounts[os] = (stats.osCounts[os] || 0) + 1;
  });

  return stats;
};

/**
 * Valid authorities constant
 */
export const VALID_AUTHORITIES = Object.freeze(['Admin', 'Office', 'DM', 'AS']);

/**
 * Normalize and validate authorities array
 * @param {string|string[]} authorities - Authority or array of authorities
 * @returns {string[]} Validated authorities array
 */
export const normalizeAuthorities = (authorities) => {
  if (!authorities) return [];
  const arr = Array.isArray(authorities) ? authorities : [authorities];
  const validSet = new Set(VALID_AUTHORITIES);
  return Array.from(new Set(arr.map((a) => String(a).trim()))).filter((a) =>
    validSet.has(a)
  );
};

// ==================== PDF & Email Endpoints ====================

/**
 * Download a form's PDF using the encrypted token URL
 * @param {string} pdfDownloadUrl - The full encrypted URL path (e.g., "/api/v1/admin/forms/bonus/aB3x.../pdf")
 * @returns {Promise<Blob>} PDF blob data
 */
export const downloadFormPdf = async (pdfDownloadUrl) => {
  if (!pdfDownloadUrl) {
    throw new Error('PDF download URL is required');
  }
  const response = await axios.get(`${backendUrl}${pdfDownloadUrl}`, {
    responseType: 'blob',
  });
  return response;
};

/**
 * Resend a failed email notification for a form
 * @param {string} formType - Form type (e.g., "bonus", "mileage")
 * @param {string} formToken - Encrypted form token
 * @returns {Promise<Object>} Response with updated emailStatus
 */
export const resendFormEmail = async (formType, formToken) => {
  if (!formType || !formToken) {
    throw new Error('Form type and form token are required');
  }
  const response = await axios.post(
    `${backendUrl}/api/v1/admin/forms/${encodeURIComponent(
      formType
    )}/${encodeURIComponent(formToken)}/resend-email`
  );
  return response.data;
};

/**
 * Extract formType and formToken from a pdfDownloadUrl
 * URL format: /api/v1/admin/forms/{formType}/{formToken}/pdf
 * @param {string} pdfDownloadUrl - The encrypted PDF URL
 * @returns {{ formType: string, formToken: string } | null}
 */
export const extractFormToken = (pdfDownloadUrl) => {
  if (!pdfDownloadUrl) return null;
  const match = pdfDownloadUrl.match(
    /\/api\/v1\/admin\/forms\/([^/]+)\/([^/]+)\/pdf/
  );
  if (!match) return null;
  return { formType: match[1], formToken: match[2] };
};

// ==================== Default Export ====================

const adminApiService = {
  // Forms
  fetchAllForms,
  fetchFormsByUser,
  fetchFormsPaginated,
  fetchFormsPaginatedByUser,
  fetchRecentForms,
  fetchFormCount,
  submitForm,
  // PDF & Email
  downloadFormPdf,
  resendFormEmail,
  extractFormToken,
  // Users
  fetchAllUsers,
  fetchUserByUsername,
  deleteUser,
  assignUserAuthorities,
  replaceUserAuthorities,
  // Districts
  fetchAllDistricts,
  deleteDistrict,
  enableDistrict,
  // Stores
  fetchAllStores,
  deleteStore,
  // Sessions
  fetchAllSessions,
  revokeSession,
  cleanupExpiredTokens,
  // Utilities
  calculateSessionStats,
  normalizeAuthorities,
  VALID_AUTHORITIES,
};

export default adminApiService;
